import React from 'react'

import {
  AlignItems,
  BorderStyle,
  Box,
  Color,
  Display,
  FontWeight,
  JustifyContent,
  Link,
  LinkVariant,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import type { MenuExperienceNavItem } from './hooks/useMenuExperienceNavigation'

type MenuExperienceCategoryNavProps = {
  links: MenuExperienceNavItem[]
  pathname?: string
  query: Record<string, unknown>
}

const MenuExperienceCategoryLink = withCitrusTheme(Link, (theme) => ({
  alignItems: AlignItems.Center,
  color: theme.color(Color.ColdGrey_900),
  cursor: 'pointer',
  display: Display.Flex,
  fontWeight: FontWeight.Bold,
  justifyContent: JustifyContent.Center,
  minHeight: theme.spacing(12),
  textDecoration: 'none',
  width: 'min(33.33%, 12rem)',
  '&:focus': {
    boxShadow: 'none',
    outline: 'none',
  },
  '&:focus-visible': {
    ...theme.radius(1.5),
    boxShadow: `inset 0 0 0 2px ${theme.color(Color.Secondary_400)}`,
    outline: 'none',
  },
  '& *': {
    whiteSpace: 'nowrap',
  },
  '&[aria-current="page"]': {
    borderBottom: `5px solid ${theme.color(Color.Secondary_400)}`,
    color: theme.color(Color.Secondary_400),
  },
}))

const MenuExperienceCategoryNavContent = withCitrusTheme(Box, () => ({
  margin: '0 auto',
}))

const addQueryParam = (searchParams: URLSearchParams, key: string, value: unknown) => {
  if (value === undefined || value === null) {
    return
  }

  if (Array.isArray(value)) {
    value.forEach((item) => addQueryParam(searchParams, key, item))

    return
  }

  if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
    return
  }

  searchParams.append(key, String(value))
}

export const buildMenuExperienceCategoryHref = ({
  pathname = '/menu',
  query,
  slug,
}: {
  pathname?: string
  query: Record<string, unknown>
  slug: string
}) => {
  const searchParams = new URLSearchParams()

  Object.entries({
    ...query,
    collection: slug,
  }).forEach(([key, value]) => addQueryParam(searchParams, key, value))

  const queryString = searchParams.toString()

  return queryString ? `${pathname}?${queryString}` : pathname
}

export const MenuExperienceCategoryNav = ({
  links,
  pathname = '/menu',
  query,
}: MenuExperienceCategoryNavProps) => (
  <Box
    data-testid="menu-experience-category-nav"
    bg={Color.White}
    borderStyle={BorderStyle.Solid}
    borderColor={Color.ColdGrey_100}
    borderBottomWidth={0.5}
  >
    <nav aria-label="Menu categories">
      <MenuExperienceCategoryNavContent
        maxWidth="1600px"
        display={Display.Flex}
        justifyContent={[JustifyContent.SpaceBetween, JustifyContent.FlexStart]}
        paddingH={[0, 4, 8]}
      >
        {links.map((link) => (
          <MenuExperienceCategoryLink
            key={link.id}
            href={buildMenuExperienceCategoryHref({ pathname, query, slug: link.slug })}
            variant={LinkVariant.None}
            data-slug={link.slug}
            aria-current={link.selected ? 'page' : undefined}
            onClick={link.onClick}
          >
            <Text
              color={link.selected ? Color.Secondary_400 : Color.ColdGrey_900}
              fontWeight={FontWeight.Bold}
              size={4}
            >
              {link.text}
            </Text>
          </MenuExperienceCategoryLink>
        ))}
      </MenuExperienceCategoryNavContent>
    </nav>
  </Box>
)
